import { PathsV1JobFunctionSearchPostParametersQuerySelect } from "@/api-schema-enums";
import { useJobFunctionByUuidQuery } from "@/common/api/job-function/use-job-function-by-uuid.query";
import { useJobFunctionCreateMutation } from "@/common/api/job-function/use-job-function-create.mutation";
import { useJobFunctionSearchQuery } from "@/common/api/job-function/use-job-function-search.query";
import { useJobFunctionUpdateMutation } from "@/common/api/job-function/use-job-function-update.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Skeleton } from "@/components/ui/skeleton";
import { SystemInfoFields, SystemInfoIdentifierType } from "@/components/system-info-fields/system-info-fields";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDebounce } from "@uidotdev/usehooks";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { z } from "zod";

export const JobFunctionFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(1, t("masterdata:NameIsRequired")).max(255, t("masterdata:JobFunctionNameMaxLength")),
    v1Id: z
      .string()
      .max(10, t("masterdata:V1IdMaxDigits"))
      .optional()
      .refine((val) => !val || /^\d+$/.test(val), {
        message: t("masterdata:V1IdMustBeValidPositiveNumber"),
      })
      .refine((val) => !val || parseInt(val, 10) <= 2147483647, {
        message: t("masterdata:V1IdMaxValue"),
      }),
  });

export type JobFunctionFormSchemaType = z.infer<ReturnType<typeof JobFunctionFormSchema>>;

interface JobFunctionFormFieldsProps {
  uuid?: string;
}

export function JobFunctionFormFields({ uuid }: JobFunctionFormFieldsProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const [_, navigate] = useLocation();
  const isUpdateMode = !!uuid;
  const [isSaving, setIsSaving] = useState(false);

  const { data: jobFunction, isPending: isJobFunctionLoading } = useJobFunctionByUuidQuery(uuid || "");

  const { mutateAsync: createMutation, isPending: isCreating } = useJobFunctionCreateMutation();
  const { mutateAsync: updateMutation, isPending: isUpdating } = useJobFunctionUpdateMutation();

  const form = useForm<JobFunctionFormSchemaType>({
    resolver: zodResolver(JobFunctionFormSchema(t)),
    defaultValues: {
      name: "",
      v1Id: "",
    },
  });

  useEffect(() => {
    if (isUpdateMode && jobFunction) {
      form.reset({
        name: jobFunction.name || "",
        v1Id: jobFunction.v1Id ? String(jobFunction.v1Id) : "",
      });
    }
  }, [jobFunction, form, isUpdateMode]);

  const [watchedName, watchedV1Id] = useWatch({ control: form.control, name: ["name", "v1Id"] });

  const debouncedName = useDebounce(watchedName, 500);
  const debouncedV1Id = useDebounce(watchedV1Id, 500);

  const { data: nameCheck, isFetching: isCheckingName } = useJobFunctionSearchQuery({
    options: {
      body: { name: [debouncedName] },
      parameters: {
        pageSize: 1,
        select: [
          PathsV1JobFunctionSearchPostParametersQuerySelect.Uuid,
          PathsV1JobFunctionSearchPostParametersQuerySelect.Name,
        ],
      },
    },
    enabled: !!debouncedName && debouncedName.trim().length > 0 && !isSaving,
  });

  const { data: v1IdCheck, isFetching: isCheckingV1Id } = useJobFunctionSearchQuery({
    options: {
      body: { v1Id: debouncedV1Id ? [Number(debouncedV1Id)] : undefined },
      parameters: {
        pageSize: 1,
        select: [
          PathsV1JobFunctionSearchPostParametersQuerySelect.Uuid,
          PathsV1JobFunctionSearchPostParametersQuerySelect.V1Id,
        ],
      },
    },
    enabled: !!debouncedV1Id && debouncedV1Id.trim().length > 0 && !Number.isNaN(Number(debouncedV1Id)) && !isSaving,
  });

  const duplicateErrors = useMemo(() => {
    const errors: string[] = [];

    if (nameCheck?.data && nameCheck.data.length > 0) {
      const duplicate = nameCheck.data[0];
      if (!isUpdateMode || duplicate.uuid !== uuid) {
        errors.push(t("masterdata:JobFunctionNameAlreadyExists", { name: debouncedName }));
      }
    }

    if (v1IdCheck?.data && v1IdCheck.data.length > 0) {
      const duplicate = v1IdCheck.data[0];
      if (!isUpdateMode || duplicate.uuid !== uuid) {
        errors.push(t("masterdata:JobFunctionV1IdAlreadyExists", { v1Id: debouncedV1Id }));
      }
    }

    return errors;
  }, [nameCheck, v1IdCheck, debouncedName, debouncedV1Id, uuid, isUpdateMode, t]);

  const onSubmit = async (values: JobFunctionFormSchemaType): Promise<void> => {
    setIsSaving(true);

    try {
      if (isUpdateMode && uuid) {
        await toastMutationStatus({
          mutationFn: () =>
            updateMutation({
              uuid,
              name: values.name,
              v1Id: values.v1Id?.trim() ? Number(values.v1Id) : null,
            }),
          successMessage: t("masterdata:JobFunctionSuccessfullyUpdated"),
          errorMessage: t("masterdata:ErrorUpdatingJobFunction"),
          onSuccess: () => {
            navigate("~/masterdata/job-function");
          },
        });
      } else {
        await toastMutationStatus({
          mutationFn: () =>
            createMutation({
              name: values.name,
              v1Id: values.v1Id?.trim() ? Number(values.v1Id) : null,
            }),
          successMessage: t("masterdata:JobFunctionSuccessfullyCreated"),
          errorMessage: t("masterdata:ErrorCreatingJobFunction"),
          onSuccess: () => {
            navigate("~/masterdata/job-function");
          },
        });
      }
    } catch (error) {
      console.error(`Error ${isUpdateMode ? "updating" : "creating"} job function:`, error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitWithValidation = async (values: JobFunctionFormSchemaType): Promise<void> => {
    if (isPending || isCheckingDuplicates) {
      return;
    }

    if (duplicateErrors.length > 0) {
      for (const error of duplicateErrors) {
        toast.error(t("masterdata:ValidationError"), {
          description: error,
          duration: 5000,
        });
      }
      return;
    }
    await onSubmit(values);
  };

  const isPending = isCreating || isUpdating;
  const isLoading = isUpdateMode && isJobFunctionLoading;
  const isCheckingDuplicates = isCheckingName || isCheckingV1Id;

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="space-y-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>

        <div className="space-y-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>

        <div className="space-y-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>

        <div className="flex justify-end space-x-4">
          <Skeleton className="h-10 w-24 rounded-md" />
          <Skeleton className="h-10 w-24 rounded-md" />
        </div>
      </div>
    );
  }

  const isSubmitDisabled = isPending || duplicateErrors.length > 0 || !form.formState.isValid || isCheckingDuplicates;

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmitWithValidation)}>
        <Card>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common:Name")} *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder={t("masterdata:JobFunctionNamePlaceholder")}
                        {...field}
                        error={duplicateErrors.some((e) => e.includes(t("masterdata:JobFunctionName")))}
                      />
                      {isCheckingName && (
                        <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                  <div className="flex min-h-5 items-center">
                    {!isCheckingName && duplicateErrors.some((e) => e.includes(t("masterdata:JobFunctionName"))) && (
                      <p className="text-error text-sm">{t("masterdata:JobFunctionNameDuplicateError")}</p>
                    )}
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="v1Id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:V1ID")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder={t("masterdata:V1IdExample")}
                        {...field}
                        error={duplicateErrors.some((e) => e.includes(t("masterdata:V1ID")))}
                      />
                      {isCheckingV1Id && (
                        <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                  <div className="flex min-h-5 items-center">
                    {!isCheckingV1Id && duplicateErrors.some((e) => e.includes(t("masterdata:V1ID"))) && (
                      <p className="text-error text-sm">{t("masterdata:JobFunctionV1IdDuplicateError")}</p>
                    )}
                  </div>
                </FormItem>
              )}
            />

            {isUpdateMode && (
              <SystemInfoFields
                identifiers={
                  jobFunction?.technicalId
                    ? [
                        {
                          value: jobFunction.technicalId,
                          label: { kind: "known", type: SystemInfoIdentifierType.TechnicalId },
                        },
                      ]
                    : undefined
                }
                uuid={jobFunction?.uuid}
                createdAt={jobFunction?.createdAt}
                updatedAt={jobFunction?.updatedAt}
              />
            )}
          </CardContent>
        </Card>

        <FormCardActions
          cancelLabel={t("common:Cancel")}
          onCancel={() => navigate("~/masterdata/job-function")}
          isPending={isPending}
          submitDisabled={isSubmitDisabled}
          submitLabel={t("common:Save")}
        />
      </form>
    </Form>
  );
}
